import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import './App.css';
import Home from './Home';
import InstantGuide from './InstantGuide';
import ChainLinkPro from './ChainLinkPro';
import ChainLinkPro2 from './ChainLinkPro2';
import PrivacyPolicy from './PrivacyPolicy';
import InstantGuideAccountData from './InstantGuideAccountData';

/* ============================================================
   DOCUMENT HEAD (title + meta description per page)
   ============================================================ */
const PAGE_HEADS = {
  '/': {
    title: 'Software Beyond Limits — Innovative Mobile Apps',
    description: 'Creating innovative mobile applications that make a difference.',
  },
  '/apps/instant-guide': {
    title: 'Instant Guide — Stories, Secrets & Surprises at Every Landmark',
    description: 'Deep audio tours with stories, secrets, history, art and fun facts. Unlock hidden nuggets at 11,000+ places. Introduction audio free. Offline. Six languages.',
  },
  '/apps/chain-link-pro': {
    title: 'Chain Link Pro — Software Beyond Limits',
    description: 'Chain Link Pro by Software Beyond Limits. Discover our puzzle game app.',
  },
  '/apps/chain-link-pro-2': {
    title: 'Chain Link Pro 2 — Software Beyond Limits',
    description: 'Chain Link Pro 2 by Software Beyond Limits. Discover our puzzle game app.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy — Instant Guide | Software Beyond Limits',
    description:
      'Privacy Policy for Instant Guide by Software Beyond Limits. How we collect, use, and protect your data.',
  },
  '/apps/instant-guide/account-and-data': {
    title: 'Instant Guide — Account Policy',
    description: 'How to create an Instant Guide account and how to request deletion of your account and data. Software Beyond Limits.',
  },
};

function setMeta(nameOrProp, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProp);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function DocumentHead() {
  const location = useLocation();

  useEffect(() => {
    const head = PAGE_HEADS[location.pathname] || PAGE_HEADS['/'];
    const baseUrl = typeof window !== 'undefined'
      ? window.location.origin + location.pathname
      : '';
    const publicUrl = process.env.PUBLIC_URL || '';
    const imageUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${publicUrl}/app_icon.jpg`
      : '';

    document.title = head.title;
    setMeta('description', head.description);
    setMeta('og:title', head.title, true);
    setMeta('og:description', head.description, true);
    setMeta('og:type', 'website', true);
    if (baseUrl) {
      setMeta('og:url', baseUrl, true);
      setCanonical(baseUrl);
    }
    if (imageUrl) {
      setMeta('og:image', imageUrl, true);
      setMeta('twitter:image', imageUrl);
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', head.title);
    setMeta('twitter:description', head.description);
  }, [location.pathname]);

  return null;
}

/* ============================================================
   HEADER
   ============================================================ */
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile nav when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`site-header${scrolled ? ' site-header--scrolled' : ''}${mobileOpen ? ' site-header--mobile-open' : ''}`}
    >
      <div className="site-header__inner">
        <Link to="/" className="site-header__brand" onClick={() => setMobileOpen(false)}>
          <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="Software Beyond Limits" className="site-header__brand-icon" />
          <span className="site-header__brand-name">Software Beyond Limits</span>
        </Link>

        {/* Desktop nav */}
        <nav className="site-header__nav">
          {isHome ? (
            <>
              <button className="nav-link" onClick={() => scrollTo('home')}>Home</button>
              <button className="nav-link" onClick={() => scrollTo('apps')}>Apps</button>
              <button className="nav-link" onClick={() => scrollTo('about')}>About</button>
            </>
          ) : (
            <>
              <Link to="/" className="nav-link">Home</Link>
              <Link to="/" className="nav-link" state={{ scrollTo: 'apps' }}>Apps</Link>
              <Link to="/" className="nav-link" state={{ scrollTo: 'about' }}>About</Link>
            </>
          )}
          <a href="mailto:softwarebeyondlimits@gmail.com" className="nav-link">Contact</a>
        </nav>

        {/* Mobile toggle */}
        <button
          className="site-header__mobile-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="site-header__mobile-nav">
          {isHome ? (
            <>
              <button className="nav-link" onClick={() => scrollTo('home')}>Home</button>
              <button className="nav-link" onClick={() => scrollTo('apps')}>Apps</button>
              <button className="nav-link" onClick={() => scrollTo('about')}>About</button>
            </>
          ) : (
            <>
              <Link to="/" className="nav-link" onClick={() => setMobileOpen(false)}>Home</Link>
              <Link to="/" className="nav-link" state={{ scrollTo: 'apps' }} onClick={() => setMobileOpen(false)}>Apps</Link>
              <Link to="/" className="nav-link" state={{ scrollTo: 'about' }} onClick={() => setMobileOpen(false)}>About</Link>
            </>
          )}
          <a href="mailto:softwarebeyondlimits@gmail.com" className="nav-link">Contact</a>
        </div>
      )}
    </header>
  );
}

/* ============================================================
   FOOTER
   ============================================================ */
function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="Software Beyond Limits" className="site-footer__brand-icon" />
          <span className="site-footer__brand-name">Software Beyond Limits</span>
        </div>
        <p className="site-footer__tagline">
        Creating innovative mobile applications that are fun to use.
        </p>
        <a href="mailto:softwarebeyondlimits@gmail.com" className="site-footer__email">
          <span>✉</span> softwarebeyondlimits@gmail.com
        </a>
        <div className="site-footer__divider" />
        <div className="site-footer__bottom">
          <p>© {new Date().getFullYear()} Software Beyond Limits. All rights reserved.</p>
          <Link to="/privacy-policy" className="site-footer__privacy">Privacy Policy</Link>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================
   SCROLL TO SECTION (after navigation)
   ============================================================ */
function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    // Handle scrollTo state when navigating from another page
    if (location.state && location.state.scrollTo) {
      const id = location.state.scrollTo;
      const attempt = (retries) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else if (retries > 0) {
          setTimeout(() => attempt(retries - 1), 100);
        }
      };
      setTimeout(() => attempt(5), 100);
    } else if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return null;
}

/* ============================================================
   LAYOUT WRAPPERS
   ============================================================ */
function HomeLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function AppPageLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
    </>
  );
}

/* ============================================================
   APP ROOT
   ============================================================ */
function App() {
  return (
    <BrowserRouter>
      <DocumentHead />
      <ScrollManager />
      <Routes>
        <Route path="/" element={<HomeLayout><Home /></HomeLayout>} />
        <Route path="/apps/instant-guide" element={<AppPageLayout><InstantGuide /></AppPageLayout>} />
        <Route path="/apps/chain-link-pro" element={<AppPageLayout><ChainLinkPro /></AppPageLayout>} />
        <Route path="/apps/chain-link-pro-2" element={<AppPageLayout><ChainLinkPro2 /></AppPageLayout>} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/apps/instant-guide/account-and-data" element={<InstantGuideAccountData />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
